import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Alert, Button, Spinner } from "react-bootstrap";
import { startOfMonth } from "date-fns";
import { fetchTeamOverview } from "../../../redux/slices/absenceSlice";
import { fetchProjects } from "../../../redux/slices/projectsSlice";
import { fetchDelegations } from "../../../redux/slices/delegationSlice";
import { fetchTeams } from "../../../redux/slices/teamSlice";
import TeamOverviewToolbar from "./TeamOverviewToolbar";
import TeamOverviewGrid from "./TeamOverviewGrid";
import TeamOverviewLegend from "./TeamOverviewLegend";
import { getPresentAbsenceTypes, sortUsersByName } from "./teamOverviewUtils";
import "./TeamOverview.css";

const SEARCH_DEBOUNCE_MS = 300;

const SCOPE_SOURCES = {
  project: { selector: (state) => state.projects?.items, fetch: fetchProjects },
  delegation: { selector: (state) => state.delegations?.items, fetch: fetchDelegations },
  team: { selector: (state) => state.teams?.items, fetch: fetchTeams },
};

const EMPTY = [];

const TeamOverview = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [scope, setScope] = useState("all");
  const [scopeId, setScopeId] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const { users, isLoading, error } = useSelector((state) => state.absences.teamOverview);
  const scopeOptions = useSelector((state) => SCOPE_SOURCES[scope]?.selector(state)) || EMPTY;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const source = SCOPE_SOURCES[scope];
    if (source && scopeOptions.length === 0) dispatch(source.fetch());
  }, [dispatch, scope, scopeOptions.length]);

  const isScopeReady = scope === "all" || Boolean(scopeId);

  useEffect(() => {
    if (!isScopeReady) return;
    dispatch(
      fetchTeamOverview({
        year: month.getFullYear(),
        month: month.getMonth() + 1,
        scope,
        scopeId: scope === "all" ? undefined : scopeId,
        search: debouncedSearch,
      }),
    );
  }, [dispatch, month, scope, scopeId, debouncedSearch, isScopeReady, reloadKey]);

  const sortedUsers = useMemo(() => sortUsersByName(users || [], i18n.language), [users, i18n.language]);
  const absenceTypes = useMemo(() => getPresentAbsenceTypes(users || []), [users]);

  const handleScopeChange = (nextScope) => {
    setScope(nextScope);
    setScopeId("");
  };

  const renderBody = () => {
    if (error) {
      return (
        <Alert variant="danger" className="d-flex justify-content-between align-items-center">
          <span>{error}</span>
          <Button size="sm" variant="outline-danger" onClick={() => setReloadKey((key) => key + 1)}>
            {t("components.Calendar.teamOverview.retry")}
          </Button>
        </Alert>
      );
    }
    if (!isScopeReady) {
      return <p className="team-overview__empty">{t(`components.Calendar.teamOverview.select_${scope}`)}</p>;
    }
    if (!isLoading && sortedUsers.length === 0) {
      return <p className="team-overview__empty">{t("components.Calendar.teamOverview.no_people")}</p>;
    }
    return <TeamOverviewGrid month={month} users={sortedUsers} />;
  };

  return (
    <div className="team-overview">
      <TeamOverviewToolbar
        month={month}
        onMonthChange={setMonth}
        scope={scope}
        onScopeChange={handleScopeChange}
        scopeId={scopeId}
        onScopeIdChange={setScopeId}
        scopeOptions={scopeOptions}
        search={search}
        onSearchChange={setSearch}
      />
      <TeamOverviewLegend absenceTypes={absenceTypes} />
      <div className="team-overview__body" aria-busy={isLoading}>
        {isLoading && (
          <div className="team-overview__loading">
            <Spinner animation="border" size="sm" role="status" aria-label={t("components.Calendar.teamOverview.loading")} />
          </div>
        )}
        {renderBody()}
      </div>
    </div>
  );
};

export default TeamOverview;
